/**
 * Feature Banner block entry.
 *
 * Importing the SCSS here makes Vite bundle the block's CSS alongside its JS,
 * so the theme ships both together.
 *
 * Two jobs, both of them things CSS cannot do on its own:
 *
 * 1. The entrance. The section sits below the fold, so a plain CSS animation
 *    would play out before anyone scrolled to it. The `data-reveal` attribute is
 *    set here and only here — with the script blocked the attribute never
 *    appears and the section renders in full, so the copy is never hidden
 *    behind JavaScript.
 * 2. The hotspot tooltips. Hover and keyboard focus open them in CSS alone;
 *    what is left is Escape dismissing an open tooltip without moving focus
 *    (WCAG 1.4.13) and touch devices, which have no hover to open one with.
 */

import './index.scss';

const banners = document.querySelectorAll('.feature-banner');

if (banners.length) {
    const OPEN = 'data-dismissed';

    /**
     * Re-arm every tooltip in a section after a dismissal.
     *
     * @param {Element} banner Section root.
     * @returns {void}
     */
    const clearDismissed = (banner) => {
        for (const spot of banner.querySelectorAll(`[${OPEN}]`)) {
            spot.removeAttribute(OPEN);
        }
    };

    // Older browsers without the observer keep the section in its end state
    // rather than animating it — degraded decoration, never missing content.
    const observer =
        'IntersectionObserver' in window
            ? new IntersectionObserver(
                  (entries) => {
                      for (const entry of entries) {
                          if (entry.isIntersecting) {
                              entry.target.setAttribute('data-reveal', 'in');
                              observer.unobserve(entry.target);
                          }
                      }
                  },
                  { rootMargin: '0px 0px -15% 0px' }
              )
            : null;

    for (const banner of banners) {
        if (observer) {
            banner.setAttribute('data-reveal', 'pending');
            observer.observe(banner);
        }

        for (const hotspot of banner.querySelectorAll('.feature-banner__hotspot')) {
            const trigger = hotspot.querySelector('.feature-banner__hotspot-trigger');

            if (!trigger) {
                continue;
            }

            // Pointer or keyboard re-entry means the visitor wants it back.
            hotspot.addEventListener('pointerenter', () => hotspot.removeAttribute(OPEN));
            trigger.addEventListener('focus', () => hotspot.removeAttribute(OPEN));

            // A button trigger has no navigation to perform, so on touch it
            // doubles as the open/close control.
            if ('BUTTON' === trigger.tagName) {
                trigger.addEventListener('click', () => {
                    const dismissed = 'true' === hotspot.getAttribute(OPEN);
                    clearDismissed(banner);

                    if (!dismissed) {
                        hotspot.setAttribute(OPEN, 'true');
                    }
                });
            }
        }

        banner.addEventListener('keydown', (event) => {
            if ('Escape' !== event.key) {
                return;
            }

            // Focus beats hover: Escape is a keyboard action, and a single
            // selector would hand back whichever dot came first in the
            // document — dismissing the wrong tooltip whenever the pointer
            // happens to rest on one dot while another holds focus.
            const open =
                banner.querySelector('.feature-banner__hotspot:focus-within') ||
                banner.querySelector('.feature-banner__hotspot:hover');

            if (open) {
                // Dismiss only — focus deliberately stays where it is.
                open.setAttribute(OPEN, 'true');
            }
        });
    }
}
